import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { shareReplay, tap } from 'rxjs/operators';

/**
 * CacheService handles long-term caching for semi-static data like Menus and Enums.
 * It uses shareReplay(1) to ensure that multiple subscribers get the same data
 * without triggering multiple API calls.
 */
@Injectable({
    providedIn: 'root'
})
export class CacheService {
    private cache = new Map<string, Observable<any>>();

    constructor() { }

    /**
     * Returns a cached observable for the given key, or creates a new one using the provided factory.
     * @param key Unique key for the cached data
     * @param factory function that returns an Observable (usually an API call)
     */
    get<T>(key: string, factory: () => Observable<T>): Observable<T> {
        if (!this.cache.has(key)) {
            const observable = factory().pipe(
                shareReplay(1),
                tap({
                    error: () => this.cache.delete(key) // Remove from cache on error to allow retry
                })
            );
            this.cache.set(key, observable);
        }
        return this.cache.get(key) as Observable<T>;
    }

    /**
     * Force refresh the cache for a specific key
     */
    refresh<T>(key: string, factory: () => Observable<T>): Observable<T> {
        this.cache.delete(key);
        return this.get(key, factory);
    }

    /**
     * Delete a specific key from the cache
     */
    delete(key: string): void {
        this.cache.delete(key);
    }

    /**
     * Delete all keys starting with a specific prefix
     */
    deleteByPrefix(prefix: string): void {
        for (const key of this.cache.keys()) {
            if (key.startsWith(prefix)) {
                this.cache.delete(key);
            }
        }
    }

    /**
     * Clear the entire cache (e.g., on logout)
     */
    clear(): void {
        this.cache.clear();
    }
}
